import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useCompanyInfo } from '../contexts/CompanyInfoContext';
import { getMediaUrl } from '../utils/mediaUrl';

const SITE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Builds OpeningHoursSpecification from dynamic opening_hours data
 */
const buildOpeningHoursSpec = (hours) => {
  if (!hours) return [];
  const dayMap = {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
  };
  const specs = [];
  // Group consecutive days with same hours
  const groups = {};
  for (const [day, info] of Object.entries(hours)) {
    if (info?.is_open && info.open && info.close) {
      const key = `${info.open}-${info.close}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(dayMap[day]);
    }
  }
  for (const [timeRange, days] of Object.entries(groups)) {
    const [opens, closes] = timeRange.split('-');
    specs.push({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": days,
      "opens": opens,
      "closes": closes
    });
  }
  return specs;
};

/**
 * Builds the LocalBusiness (AutoRepair) JSON-LD schema from company_info data
 */
const buildLocalBusinessSchema = (info, logoUrl) => {
  const address = info?.address || {};
  const contact = info?.contact || {};
  const coords = info?.coordinates || {};
  const social = info?.social_media || {};

  const sameAs = [social.facebook, social.instagram, social.linkedin].filter(Boolean);

  return {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "@id": `${SITE_URL}/#organization`,
    "name": info?.company_name || 'Sand-tec AS',
    "legalName": info?.company_name || 'Sand-tec AS',
    "url": SITE_URL,
    ...(logoUrl ? { "logo": logoUrl, "image": logoUrl } : {}),
    "description": info?.tagline ? `${info.tagline} av tungbil, tilhenger og personbil. Moderne verksted i ${address.city || 'Sagstua'}.` : undefined,
    "telephone": contact.phone || undefined,
    "email": contact.email || undefined,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address.street,
      "addressLocality": address.city,
      "postalCode": address.postal_code,
      "addressRegion": address.county,
      "addressCountry": "NO"
    },
    ...(coords.lat && coords.lng ? {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": coords.lat,
        "longitude": coords.lng
      }
    } : {}),
    "openingHoursSpecification": buildOpeningHoursSpec(info?.opening_hours),
    "priceRange": "$$",
    ...(sameAs.length > 0 ? { "sameAs": sameAs } : {}),
    ...(coords.lat && coords.lng ? {
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": coords.lat,
          "longitude": coords.lng
        },
        "geoRadius": "50000"
      }
    } : {}),
    ...(info?.certifications?.length > 0 ? {
      "hasCredential": info.certifications.map(c => ({
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "certification",
        "name": c
      }))
    } : {}),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Verkstedtjenester",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "EU-kontroll",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EU-kontroll tungbil" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EU-kontroll tilhenger" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EU-kontroll personbil" } }
          ]
        }
      ]
    }
  };
};

const buildWebSiteSchema = (info) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  "url": SITE_URL,
  "name": info?.company_name || 'Sand-tec AS',
  "description": info?.tagline || '',
  "publisher": { "@id": `${SITE_URL}/#organization` },
  "inLanguage": "nb-NO"
});

const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const getServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": service.name,
  "name": service.name,
  "description": service.description,
  "provider": { "@id": `${SITE_URL}/#organization` },
  "areaServed": { "@type": "Country", "name": "Norway" },
  "offers": service.price ? { "@type": "Offer", "price": service.price, "priceCurrency": "NOK" } : undefined
});

export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
  }))
});

export const getAggregateRatingSchema = (name, rating, reviewCount) => ({
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  "name": name,
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": rating,
    "reviewCount": reviewCount,
    "bestRating": "5",
    "worstRating": "1"
  }
});

const SEO = ({
  title, description, canonical, image, type = 'website',
  noindex = false, breadcrumbs = null, service = null, faqs = null, rating = null,
  titleOverride = false
}) => {
  const companyInfo = useCompanyInfo();
  const companyName = companyInfo?.company_name || 'Sand-tec AS';
  const tagline = companyInfo?.tagline || '';
  const logoPath = companyInfo?.logo;
  const logoUrl = logoPath ? getMediaUrl(logoPath) : `${SITE_URL}/images/sand-tec-logo-light.png`;

  const fullTitle = titleOverride ? title : title ? `${title} | ${companyName}` : `${companyName} | ${tagline}`;
  const fullDescription = description || companyInfo?.seo?.meta_description || `${tagline} av tungbil, tilhenger og personbil.`;
  const canonicalUrl = canonical || SITE_URL;
  const ogImage = image || logoUrl;
  const seo = companyInfo?.seo || {};

  const structuredData = [
    buildLocalBusinessSchema(companyInfo, logoUrl),
    buildWebSiteSchema(companyInfo)
  ];
  if (breadcrumbs) structuredData.push(getBreadcrumbSchema(breadcrumbs));
  if (service) structuredData.push(getServiceSchema(service));
  if (faqs?.length > 0) structuredData.push(getFAQSchema(faqs));
  if (rating) structuredData.push(getAggregateRatingSchema(companyName, rating.value, rating.count));

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {seo.google_site_verification && <meta name="google-site-verification" content={seo.google_site_verification} />}
      {seo.bing_site_verification && <meta name="msvalidate.01" content={seo.bing_site_verification} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="nb_NO" />
      <meta property="og:site_name" content={companyName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
export { buildLocalBusinessSchema as getLocalBusinessSchema, buildWebSiteSchema as getWebSiteSchema };
